import { defaultAllSelection } from "services/constants/selections";
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import { customGetAPI } from "services/utils/CustomAPI";
import { designColors } from "services/constants/designTokens";
import { BulkReprocessEmployeePicker } from "pages/HR/EmployeeOnboard/BulkReprocessEmployeePicker";
import { isActiveRecord, isGroupMember, membershipCompanyId, membershipKey, membershipUserId } from "./membershipRules";

// Finds a user's employee record (department, designation, shift) in the
// home company's employee list. User-directory IDs are used on save;
// employee records only supply department, designation and shift details.
const findEmployeeRecord = (records, user) =>
  records.find(
    (record) =>
      String(record._source_company_id) === String(user.home_company_id ?? user.company_id) && (
      String(record?.user_id ?? record?.id) === String(user.id) ||
      (user.user_code && record?.user_code === user.user_code) ||
      (user.employee_id && record?.employee_id === user.employee_id)),
  );

// new_user_details returns a user's departments as one comma-joined text
// ("Alpha, Beta") or as a list when they belong to several. Split it into
// the real department names, the same way the Users list does.
export const departmentNames = (value) =>
  (Array.isArray(value) ? value : typeof value === "string" ? value.split(",") : [value])
    .map((item) =>
      typeof item === "string"
        ? item
        : item?.department_name ?? item?.department ?? item?.name ?? "")
    .map((name) => String(name ?? "").trim())
    .filter(Boolean)
    .filter((name, index, all) => all.indexOf(name) === index);

// By Department rows (2026-10-08, user request): the same two calls as
// People > Departments, made once with the All Companies id, as that screen
// does (user decision 2026-10-07) -- not once per company. A department
// returned more than once is one row here. Two different departments
// sharing a name get their company added to the label when it is known.
export const mergeDepartments = (records, companies = []) => {
  const rows = [];
  records.forEach((record) => {
    const key = String(record.departmentId);
    const source = { companyId: record.companyId, leadId: record.leadId, departmentId: record.departmentId };
    const row = rows.find((item) => item.key === key);
    if (row) row.sources.push(source);
    else rows.push({ key, name: String(record.name ?? "").trim(), sources: [source] });
  });
  const companyName = (row) => companies.find((company) =>
    String(company.id) === String(row.sources[0].companyId))?.companyname;
  return rows
    .map((row) => (rows.some((other) => other !== row && other.name === row.name) && companyName(row)
      ? { ...row, name: `${row.name} (${companyName(row)})` }
      : row))
    .sort((a, b) => a.name.localeCompare(b.name));
};

// get_department_members rows only say who is in a department. The person
// that gets ticked is the matching entry of the picker's own user list, so
// the saved id, the membership company and the "already in this group"
// rule stay exactly as in By Employee. Members who cannot be added (already
// in the group, inactive, no user account) are left out.
export const pickableMembers = (members, pickerUsers) =>
  members
    .map((member) => {
      const memberId = member?.user_id ?? member?.id;
      return pickerUsers.find((user) =>
        (memberId != null && String(membershipUserId(user)) === String(memberId)) ||
        (member?.user_code && user.user_code &&
          String(member.user_code).trim() === String(user.user_code).trim() &&
          // An All Companies response does not say whose user_code it is.
          (String(member._source_company_id) === String(defaultAllSelection) ||
            String(user.home_company_id ?? "") === String(member._source_company_id ?? ""))));
    })
    .filter((user, index, all) => user && all.indexOf(user) === index);

// "Add Users" picker for Add/Edit User Group. Reuses the Bulk Reprocess
// employee picker (By Employee / By Department / Advanced Filter) so users
// are picked the same way as there.
// No company filter (2026-10-07, user request): the list shows the
// employees of every available company at once, and ticking one adds that
// user. sourceCompanies supplies the home companies to load users from;
// companies are the group's assigned companies. Each user is added with
// their own company (see membershipCompanyId).
// inline (2026-10-01, user request): renders inside the
// group form's own Users section, in place of the users table, instead of
// opening a second side panel over the form. Inline mode has no footer of
// its own: the picker sits above a long employee list that does not scroll
// on its own, so its buttons would be pushed out of sight. The form shows
// Cancel / "Add Selected Users" in its sticky drawer footer instead, fed by
// onSelectionChange.
export const UserGroupUserPicker = ({
  open,
  inline = false,
  onSelectionChange,
  companies,
  sourceCompanies = companies,
  users,
  members = [],
  onClose,
  onAdd,
}) => {
  const { domain } = useSelector((state) => state.tokenReducer);
  const [selected, setSelected] = useState([]);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [records, setRecords] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [departmentSources, setDepartmentSources] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [departmentsError, setDepartmentsError] = useState("");
  // Per department key: { loading } | { error } | { members } (raw API rows).
  const [departmentState, setDepartmentState] = useState({});
  // One request per department; a new department list drops the old ones.
  const departmentRequests = useRef({});
  const departmentRun = useRef(0);
  const pickerUsersRef = useRef([]);

  const companyIds = useMemo(
    () => [...new Set(companies.map((company) => String(company.id)))],
    [companies],
  );
  const companyKey = companyIds.join(",");
  const sourceCompanyIds = [...new Set(sourceCompanies
    .filter(isActiveRecord)
    .map((company) => String(company.id))
    .filter((id) => id !== String(defaultAllSelection) && id !== "undefined" && id !== "null" && id !== ""))];
  const sourceCompanyKey = sourceCompanyIds.join(",");

  // Fresh start on every open.
  useEffect(() => {
    if (!open) return;
    setSelected([]);
  }, [open]);

  useEffect(() => {
    onSelectionChange?.(selected);
    // onSelectionChange is a plain setter from the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // The group form's company rows can change while the picker is open, and
  // that can change which company a ticked user would be saved under.
  // Start the selection again so no stale membership company is added.
  useEffect(() => {
    setSelected((prev) => (prev.length ? [] : prev));
    // Runs on a change in the set of assigned companies only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyKey]);

  // Load each available home company's directory explicitly. Global
  // un-grouped lists can exclude users belonging to other groups.
  // Never request the unscoped/All Companies user-directory endpoint.
  useEffect(() => {
    setSelected([]);
    setEligibleUsers([]);
    setRecords([]);
    setError("");
    if (!open) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all(sourceCompanyIds.map(async (sourceCompanyId) => {
      const companyQuery = encodeURIComponent(sourceCompanyId);
      const [directory, employees] = await Promise.all([
        customGetAPI(`${domain}new_user_details/?company_id=${companyQuery}`),
        customGetAPI(`${domain}list/employee?company_id=${companyQuery}&department_id=${defaultAllSelection}&status=Active`).catch(() => null),
      ]);
      if (!directory?.ok) throw new Error("Couldn't load users from all available companies. Please try again.");
      const data = directory.responseDetails;
      const employeeData = employees?.ok ? employees.responseDetails : [];
      return {
        users: (Array.isArray(data) ? data : data?.results ?? [])
          .filter((user) => isActiveRecord(user) &&
            (!user.status || String(user.status).toLowerCase() === "active"))
          .map((user) => ({
            ...user,
            id: membershipUserId(user),
            name: user.user_name ?? user.name,
            home_company_id: user.company_id ?? sourceCompanyId,
            home_company: user.company_name ?? sourceCompanies.find((company) => String(company.id) === sourceCompanyId)?.companyname ?? "",
          })),
        records: (Array.isArray(employeeData) ? employeeData : employeeData?.results ?? [])
          .map((record) => ({ ...record, _source_company_id: sourceCompanyId })),
      };
    })).then((sources) => {
      if (cancelled) return;
      if (!sources.length) throw new Error("No companies are available to load users.");
      // A user returned by more than one company's directory is listed once.
      const all = sources.flatMap((source) => source.users);
      setEligibleUsers(all.filter((user, index) =>
        user.id == null || all.findIndex((item) => String(item.id) === String(user.id)) === index));
      setRecords(sources.flatMap((source) => source.records));
    }).catch((failure) => {
      if (!cancelled) setError(failure.message);
    }).finally(() => {
      if (cancelled) return;
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // sourceCompanyKey captures changes to the set of source company IDs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sourceCompanyKey, domain]);

  // Department names for By Department: People > Departments' list call,
  // for All Companies. Their employees are loaded on expand (loadDepartment).
  useEffect(() => {
    departmentRun.current += 1;
    const run = departmentRun.current;
    departmentRequests.current = {};
    setDepartmentSources([]);
    setDepartmentState({});
    setDepartmentsError("");
    if (!open) {
      setDepartmentsLoading(false);
      return;
    }
    setDepartmentsLoading(true);
    Promise.all([String(defaultAllSelection)].map(async (companyId) => {
      const res = await customGetAPI(
        `${domain}get_leads_based_on_departments/?company_id=${encodeURIComponent(companyId)}`);
      if (!res?.ok) throw new Error("Couldn't load departments. Please try again.");
      const data = res.responseDetails;
      return (Array.isArray(data) ? data : data?.results ?? [])
        // The "All" row is a filter option, not a department.
        .filter((department) => department?.department_name !== "All" &&
          (department?.department_id ?? department?.id) != null &&
          String(department?.department_id ?? department?.id) !== String(defaultAllSelection))
        .map((department) => ({
          companyId,
          departmentId: department.department_id ?? department.id,
          leadId: department.department_lead_id,
          name: department.department_name,
        }));
    })).then((lists) => {
      if (run === departmentRun.current) setDepartmentSources(mergeDepartments(lists.flat(), sourceCompanies));
    }).catch((failure) => {
      if (run === departmentRun.current) setDepartmentsError(failure.message);
    }).finally(() => {
      if (run === departmentRun.current) setDepartmentsLoading(false);
    });
    // sourceCompanies only supplies company names for duplicate labels.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, domain]);

  // Loads one department's employees (People > Departments' members call)
  // the first time it is expanded or ticked; resolves to the users of that
  // department that can be added.
  const loadDepartment = (key) => {
    const department = departmentSources.find((item) => item.key === key);
    if (!department) return Promise.resolve([]);
    const run = departmentRun.current;
    if (!departmentRequests.current[key]) {
      setDepartmentState((prev) => ({ ...prev, [key]: { loading: true } }));
      const request = Promise.all(department.sources.map(async (source) => {
        const res = await customGetAPI(
          `${domain}get_department_members/${source.leadId}/${source.departmentId}/?company_id=${encodeURIComponent(source.companyId)}`);
        if (!res?.ok) throw new Error("Couldn't load this department's employees. Collapse and expand it to try again.");
        const data = res.responseDetails?.members_data ?? res.responseDetails;
        return (Array.isArray(data) ? data : [])
          .map((member) => ({ ...member, _source_company_id: source.companyId }));
      })).then((lists) => lists.flat());
      departmentRequests.current[key] = request;
      request.then((members) => {
        if (run === departmentRun.current) setDepartmentState((prev) => ({ ...prev, [key]: { members } }));
      }, (failure) => {
        if (run !== departmentRun.current) return;
        delete departmentRequests.current[key];
        setDepartmentState((prev) => ({ ...prev, [key]: { error: failure.message } }));
      });
    }
    return departmentRequests.current[key]
      .then((members) => pickableMembers(members, pickerUsersRef.current));
  };

  const pickerUsers = useMemo(
    () => {
      if (loading || error || !companyIds.length) return [];
      const withMembership = eligibleUsers.map((user) => {
        const companyId = membershipCompanyId(user.home_company_id, companyIds);
        return {
          ...user,
          company_id: companyId,
          company: [...sourceCompanies, ...companies].find((company) => String(company.id) === companyId)?.companyname ??
            user.home_company ?? "",
        };
      });
      // Members removed in this Edit session keep their saved company.
      const removed = users;
      return [...withMembership, ...removed]
        .filter((user, index, pool) => user.id != null &&
          !isGroupMember(user, members) &&
          pool.findIndex((item) => membershipKey(item) === membershipKey(user)) === index)
        .map((user) => {
          const record =
            findEmployeeRecord(records, user) ?? {};
          // The employee record names the one department the person works
          // in; without it, use the department(s) on the user account.
          const departments = departmentNames(
            record.department_name ||
              record.department?.department_name ||
              (typeof record.department === "string" ? record.department : "") ||
              user.department_name ||
              user.departments ||
              user.department,
          );
          return {
            ...user,
            // Shown in the picker's Company column: the employee's own
            // company, not the company the membership is saved under.
            home_company: user.home_company ||
              sourceCompanies.find((company) => String(company.id) === String(user.home_company_id))?.companyname ||
              user.company || "",
            user_id: user.id,
            user_name: user.name,
            employee_id: user.employee_id || record.employee_id || user.user_code,
            department_name: departments.join(", "),
            // By Department lists the person under each of these.
            department_names: departments,
            designation_name: record.designation_name,
            shift_grade_name: record.shift_grade_name,
            shift_group_name: record.shift_group_name,
          };
        });
    },
    // companyKey captures changes to the set of assigned company IDs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyKey, sourceCompanyKey, companies, eligibleUsers, records, users, members, loading, error],
  );

  pickerUsersRef.current = pickerUsers;

  const departmentGroups = useMemo(
    () => departmentSources.map((department) => {
      const state = departmentState[department.key] ?? {};
      return {
        key: department.key,
        name: department.name,
        loading: Boolean(state.loading),
        error: state.error || "",
        members: state.members ? pickableMembers(state.members, pickerUsers) : undefined,
      };
    }),
    [departmentSources, departmentState, pickerUsers],
  );

  const footer = (
    <Mui.Stack direction="row" spacing={1.5} alignItems="center">
      <Mui.Typography sx={{ flex: 1, fontSize: 13, color: designColors.slate600 }}>
        {selected.length} user{selected.length === 1 ? "" : "s"} selected
      </Mui.Typography>
      <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
      <PrimaryButton disabled={!selected.length} onClick={() => onAdd(selected)}>
        Add Selected Users
      </PrimaryButton>
    </Mui.Stack>
  );

  const body = (
    <>
      <BulkReprocessEmployeePicker
        users={pickerUsers}
        selected={selected}
        onChange={setSelected}
        loading={loading}
        error={error}
        companySelected={companyIds.length > 0}
        noCompanyMessage="Add a company under Companies & Role Assignment to add users."
        emptySelectionText="Select users to add to this group."
        showCompany
        departmentGroups={departmentGroups}
        onLoadDepartment={loadDepartment}
        departmentsLoading={departmentsLoading}
        departmentsError={departmentsError}
      />
    </>
  );

  if (inline) {
    if (!open) return null;
    return <Mui.Box>{body}</Mui.Box>;
  }

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      // Opens over the group form's drawer; the expanded full view sits
      // below drawers, so this panel stays a drawer.
      hideExpand
      title="Select Users"
      subtitle="Select employees individually, by department, or using an advanced filter."
      footer={footer}
    >
      <Mui.Box sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>{body}</Mui.Box>
    </GlobalSidePanel>
  );
};
